import React, { useEffect, useState } from "react";
import PivotGridDataSource from "devextreme/ui/pivot_grid/data_source";
import PivotGrid from "devextreme-react/pivot-grid";
import { fetchPivotProjectProductWorkcell, fetchPivotProjectProductWorkcellCost } from "../api";
import { formatHoursMinutes } from "../utils/time";
import { useTranslation } from "react-i18next";
import { useCustomerCurrency } from "../context/CustomerSettingsContext";
import { formatLocalDateTime, parseLocalDateTime } from "../utils/localDateTime";
import enMessages from "devextreme/localization/messages/en.json";
import frMessages from "devextreme/localization/messages/fr.json";
import nlMessages from "devextreme/localization/messages/nl.json";
import deMessages from "devextreme/localization/messages/de.json";

const pivotMessages = {
  en: enMessages.en,
  fr: frMessages.fr,
  nl: nlMessages.nl,
  de: deMessages.de,
};

const ProjectPivotTableDevExtreme = ({ projectId, viewType = 'time', layoutType = 'hierarchical' }) => {
  const [dataSource, setDataSource] = useState(null);
  const { t, i18n } = useTranslation();
  const { currency, formatCurrency } = useCustomerCurrency();
  const messages = pivotMessages[i18n.resolvedLanguage] || pivotMessages.en;
  const texts = {
    grandTotal: messages["dxPivotGrid-grandTotal"],
    total: messages["dxPivotGrid-total"],
    noData: messages["dxDataGrid-noDataText"],
    dataNotAvailable: messages["dxPivotGrid-dataNotAvailable"],
    expandAll: messages["dxPivotGrid-expandAll"],
    collapseAll: messages["dxPivotGrid-collapseAll"],
    showFieldChooser: messages["dxPivotGrid-showFieldChooser"],
    sortColumnBySummary: messages["dxPivotGrid-sortColumnBySummary"],
    sortRowBySummary: messages["dxPivotGrid-sortRowBySummary"],
    removeAllSorting: messages["dxPivotGrid-removeAllSorting"],
  };

  useEffect(() => {
    if (projectId) {
      // Choose the API call based on the viewType
      const fetchFunction = viewType === 'time'
        ? fetchPivotProjectProductWorkcell
        : fetchPivotProjectProductWorkcellCost;

      fetchFunction(projectId).then((data) => {
        const dataWithProcessedAt = data.map((row) => ({
          ...row,
          min_processed_at_dt: row.min_processed_at ? parseLocalDateTime(row.min_processed_at) : null,
          max_processed_at_dt: row.max_processed_at ? parseLocalDateTime(row.max_processed_at) : null
        }));

        const formatProcessedAt = (value) => {
          if (!value) return '';
          return formatLocalDateTime(value, true);
        };

        // Configure different fields based on view type
        const dataField = viewType === 'time' ? 'total_time_spent' : 'total_hourly_cost';
        const dataCaption = viewType === 'time'
          ? t('projectPivot.pivot.totalTimeSpent')
          : t('projectPivot.pivot.totalCost');

        // Format configuration based on view type
        const formatSecondsToHoursMinutes = (value) => {
          if (typeof value !== 'number' || isNaN(value)) return '';
          const decimalHours = value / 3600;
          return formatHoursMinutes(decimalHours, true);
        };

        const formatConfig = viewType === 'time'
          ? { type: "fixedPoint", precision: 0, formatter: formatSecondsToHoursMinutes }
          : {
              type: "fixedPoint",
              precision: 2,
              currency,
              formatter: (value) => {
                if (typeof value !== 'number' || isNaN(value)) return '';
                return formatCurrency(value, { fallback: '' });
              }
            };

        const dateFormatConfig = { formatter: formatProcessedAt };
        const fieldsByDataField = new Map([
          ["product_id", { caption: t('projectPivot.pivot.productId'), dataField: "product_id", dataType: "number" }],
          ["product_name", { caption: t('projectPivot.pivot.productName'), dataField: "product_name", dataType: "string" }],
          ["workcell_id", { caption: t('projectPivot.pivot.workcellId'), dataField: "workcell_id", dataType: "number" }],
          ["workcell_name", { caption: t('projectPivot.pivot.workcellName'), dataField: "workcell_name", dataType: "string" }],
          ["root_product_id", { caption: t('projectPivot.pivot.rootProductId'), dataField: "root_product_id", dataType: "number" }],
          ["root_product_name", { caption: t('projectPivot.pivot.rootProductName'), dataField: "root_product_name", dataType: "string" }],
          ["operation_type", { caption: t('projectPivot.pivot.operationType'), dataField: "operation_type", dataType: "string" }],
          [
            "min_processed_at_dt",
            {
              caption: t('projectPivot.pivot.minProcessedAt'),
              dataField: "min_processed_at_dt",
              summaryType: "min",
              dataType: "date",
              format: dateFormatConfig
            }
          ],
          [
            "max_processed_at_dt",
            {
              caption: t('projectPivot.pivot.maxProcessedAt'),
              dataField: "max_processed_at_dt",
              summaryType: "max",
              dataType: "date",
              format: dateFormatConfig
            }
          ],
          [
            dataField,
            {
              caption: dataCaption,
              dataField: dataField,
              summaryType: "sum",
              format: formatConfig
            }
          ]
        ]);

        if (viewType === 'cost') {
          fieldsByDataField.set("hours_spent", {
            caption: t('projectPivot.pivot.hoursSpent'),
            dataField: "hours_spent",
            summaryType: "sum",
            format: { type: "fixedPoint", precision: 2 }
          });
          fieldsByDataField.set("hourly_rate", {
            caption: t('projectPivot.pivot.hourlyRate'),
            dataField: "hourly_rate",
            summaryType: "avg",
            format: formatConfig
          });
        }

        const createField = (fieldName, options = {}) => ({
          ...fieldsByDataField.get(fieldName),
          ...options
        });

        let fields;
        if (layoutType === 'hierarchical') {
          // Product / Workcell (hierarchical) layout
          fields = [
            createField("root_product_name", { area: "row" }),
            createField("workcell_name", { area: "row" }),
            ...(viewType === 'time' ? [
              createField("min_processed_at_dt", { area: "data" }),
              createField("max_processed_at_dt", { area: "data" })
            ] : []),
            createField(dataField, { area: "data" })
          ];
        } else {
          // Product × Workcell (crosstab) layout
          fields = [
            createField("root_product_name", { area: "row" }),
            createField("workcell_name", { area: "column" }),
            createField(dataField, { area: "data" })
          ];
        }

        const activeDataFields = new Set(fields.map((field) => field.dataField));
        fields = [
          ...fields,
          ...Array.from(fieldsByDataField.values()).filter((field) => !activeDataFields.has(field.dataField))
        ];

        const pivotGridDataSource = new PivotGridDataSource({
          fields: fields,
          store: dataWithProcessedAt,
          retrieveFields: false
        });
        setDataSource(pivotGridDataSource);
      }).catch(console.error);
    }
  }, [projectId, viewType, layoutType, t, currency, formatCurrency]);

  return (
    <div style={{ height: 400, width: "100%" }}>
      {dataSource && <PivotGrid
        dataSource={dataSource}
        texts={texts}
        allowSortingBySummary={true}
        allowSorting={true}
        allowFiltering={true}
        showBorders={true}
        showColumnGrandTotals={true}
        showRowGrandTotals={true}
        showTotalsPrior="both"
      />}
    </div>
  );
};

export default ProjectPivotTableDevExtreme;
